import type { SedeConfig } from '../../../../sedi/types'
import type { Locale } from '../../content/types'

/**
 * Opening-hours lines for the footer. Pure (imported by the footer and its
 * test). `sede.hours` is empty until the owner fills it, and the footer omits
 * the whole block then — this never renders a placeholder.
 */

export type DayCode = 'Mo' | 'Tu' | 'We' | 'Th' | 'Fr' | 'Sa' | 'Su'
export type OpeningHours = NonNullable<SedeConfig['hours']>

const WEEK: readonly DayCode[] = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

export const DAY_LABELS: Record<Locale, Record<DayCode, string>> = {
  it: { Mo: 'Lun', Tu: 'Mar', We: 'Mer', Th: 'Gio', Fr: 'Ven', Sa: 'Sab', Su: 'Dom' },
  en: { Mo: 'Mon', Tu: 'Tue', We: 'Wed', Th: 'Thu', Fr: 'Fri', Sa: 'Sat', Su: 'Sun' },
}

/**
 * Days in week order, consecutive runs joined with an en dash:
 * Mo–Fr → "Lun–Ven", Mo+We+Fr → "Lun, Mer, Ven", Mo–We+Fr → "Lun–Mer, Ven".
 */
export function formatDays(days: readonly DayCode[], locale: Locale): string {
  const labels = DAY_LABELS[locale]
  const idx = [...new Set(days.map((d) => WEEK.indexOf(d)).filter((i) => i >= 0))].sort((a, b) => a - b)
  const runs: [number, number][] = []
  for (const i of idx) {
    const last = runs[runs.length - 1]
    if (last && i === last[1] + 1) last[1] = i
    else runs.push([i, i])
  }
  return runs
    .map(([a, b]) => (a === b ? labels[WEEK[a]] : `${labels[WEEK[a]]}–${labels[WEEK[b]]}`))
    .join(', ')
}

/** One line per entry, `${dayRange} ${opens}–${closes}`; entries without a day are dropped. */
export function formatHours(hours: OpeningHours, locale: Locale): string[] {
  return hours
    .map((h) => ({ days: formatDays(h.days, locale), opens: h.opens, closes: h.closes }))
    .filter((h) => h.days && h.opens && h.closes)
    .map((h) => `${h.days} ${h.opens}–${h.closes}`)
}
